import { useEffect, useRef } from 'react';
import { URL_EVENEMENTS } from '../api/messagerieApi';
import { authStorage } from '../storage/authStorage';

/**
 * Écoute les événements admin en direct (« mail » : un courriel est arrivé dans
 * la boîte du support) et appelle `onEvenement(type, charge)` à la seconde où le
 * serveur en publie un.
 *
 * `EventSource` EST INUTILISABLE ICI : il ne sait pas porter l'en-tête
 * d'autorisation. On relit donc le flux à la main, avec `fetch`.
 *
 * SE RECONNECTE TOUT SEUL. Une connexion tenue ouverte des heures finit par
 * tomber — coupure réseau, redémarrage du serveur, mise en veille du poste,
 * jeton expiré. On retente toutes les cinq secondes, avec le jeton du moment
 * (rafraîchi entre-temps par les autres appels de l'application).
 *
 * ON REFERME LE FLUX, ON N'INTERROMPT PAS LA REQUÊTE : `reader.cancel()` termine
 * la lecture proprement (`done: true`) et le serveur voit partir son abonné
 * comme pour un onglet fermé, sans l'erreur « signal is aborted » d'un `abort()`.
 */
export function useEvenementsAdmin(onEvenement) {
  const callbackRef = useRef(onEvenement);
  callbackRef.current = onEvenement;

  useEffect(() => {
    let arrete = false;
    let lecteurActuel = null;

    const ecouter = async () => {
      while (!arrete) {
        let reponse = null;

        try {
          const jeton = authStorage.getAccessToken();

          reponse = await fetch(URL_EVENEMENTS, {
            headers: jeton ? { Authorization: `Bearer ${jeton}`, Accept: 'text/event-stream' } : { Accept: 'text/event-stream' },
            cache: 'no-store',
          });

          // Partis entre-temps : on referme ce qui vient d'arriver.
          if (arrete) return;

          if (!reponse.ok || !reponse.body) throw new Error('Flux indisponible.');

          const reader = reponse.body.getReader();
          lecteurActuel = reader;
          reponse = null;

          // Une connexion qui tombe d'elle-même erre le flux : ce rejet doit être
          // regardé, sinon il remonte comme rejet non géré.
          reader.closed.catch(() => {});

          const decoder = new TextDecoder();
          let tampon = '';

          for (;;) {
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();
            if (done || arrete) break;

            tampon += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

            const evenements = tampon.split('\n\n');
            tampon = evenements.pop() ?? '';

            evenements.forEach((evenement) => {
              // Les lignes de commentaire (« : … ») tiennent la connexion en vie :
              // aucune ligne « data: », rien à faire.
              const donnees = evenement
                .split('\n')
                .filter((l) => l.startsWith('data:'))
                .map((l) => l.slice(5).replace(/^ /, ''))
                .join('\n');
              if (!donnees) return;

              try {
                const charge = JSON.parse(donnees);
                if (charge?.type) callbackRef.current(charge.type, charge);
              } catch {
                // fragment illisible : on l'ignore plutôt que de casser le flux
              }
            });
          }
        } catch {
          if (arrete) return;
        } finally {
          lecteurActuel = null;
          // Une réponse reçue mais jamais lue garde sa connexion ouverte tant qu'on ne la referme pas.
          reponse?.body?.cancel().catch(() => {});
        }

        // La connexion est tombée : on retente dans quelques secondes plutôt que
        // de boucler à vide et de marteler le serveur.
        // eslint-disable-next-line no-await-in-loop
        if (!arrete) await new Promise((resolve) => { setTimeout(resolve, 5000); });
      }
    };

    ecouter().catch(() => {});

    return () => {
      arrete = true;
      lecteurActuel?.cancel().catch(() => {});
    };
  }, []);
}
